import React, { useMemo, useState } from 'react';

import { Tabs as MantineTabs } from '@mantine/core';
import Drawer from 'components/Drawer';
import useInvoice from 'hooks/invoice/useInvoice';
import useUpdateInvoice from 'hooks/invoice/useUpdateInvoice';
import useUpdateInvoiceNotes from 'hooks/invoice/useUpdateInvoiceNotes';
import useUpdateInvoicePaymentDate from 'hooks/invoice/useUpdateInvoicePaymentDate';

import InvoiceActivityTab from './components/InvoiceActivityTab';
import InvoiceDrawerFooter from './components/InvoiceDrawerFooter';
import InvoiceDrawerHeader from './components/InvoiceDrawerHeader';
import InvoiceInfoTab from './components/InvoiceInfoTab';
import InvoiceNotesTab from './components/InvoiceNotesTab';
import { InvoiceDrawerState } from './types';

interface InvoiceDrawerProps {
  invoiceId: string;
  state: InvoiceDrawerState;
  setState: React.Dispatch<React.SetStateAction<InvoiceDrawerState>>;
  opened: boolean;
  close: () => void;
}

const InvoiceDrawer = ({ invoiceId, state, setState, opened, close }: InvoiceDrawerProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<string | null>('info'); // info, activity, notes
  // Held here because the Payment Date picker is on the Info tab and its Save button is in the
  // footer. The spec asks for Save to stay disabled until the date actually moves.
  const [isPaymentDateDirty, setIsPaymentDateDirty] = useState<boolean>(false);

  const invoiceQuery = useInvoice(invoiceId);

  const updateInvoiceMutation = useUpdateInvoice();
  const updateInvoiceNotesMutation = useUpdateInvoiceNotes();
  const updateInvoicePaymentDateMutation = useUpdateInvoicePaymentDate();

  const handleClose = (): void => {
    setActiveTab('info');
    setState('View');
    close();
  };

  const tabs = useMemo(() => {
    const defaultTabs = [
      {
        label: 'Info',
        value: 'info',
        component: (
          <InvoiceInfoTab
            state={state}
            setState={setState}
            invoiceId={invoiceId}
            invoiceQuery={invoiceQuery}
            updateInvoiceMutation={updateInvoiceMutation}
            updateInvoicePaymentDateMutation={updateInvoicePaymentDateMutation}
            setIsPaymentDateDirty={setIsPaymentDateDirty}
          />
        ),
        isDisabled: ['Edit Notes'].includes(state),
      },
      {
        label: 'Activity',
        value: 'Activity',
        component: <InvoiceActivityTab invoiceQuery={invoiceQuery} />,
        isDisabled: ['Edit Invoice', 'Edit Notes', 'Edit Payment Date'].includes(state),
      },
      {
        label: 'Notes',
        value: 'notes',
        component: (
          <InvoiceNotesTab
            state={state}
            setState={setState}
            invoiceId={invoiceId}
            invoiceQuery={invoiceQuery}
            updateInvoiceNotesMutation={updateInvoiceNotesMutation}
          />
        ),
        isDisabled: ['Edit Invoice', 'Edit Payment Date'].includes(state),
      },
    ];
    return defaultTabs;
  }, [
    state,
    setState,
    invoiceId,
    invoiceQuery,
    updateInvoiceMutation,
    updateInvoiceNotesMutation,
    updateInvoicePaymentDateMutation,
  ]);

  return (
    <Drawer
      position='right'
      opened={opened}
      onClose={handleClose}
      title={<InvoiceDrawerHeader state={state} invoiceQuery={invoiceQuery} />}
      padding='0'
      footer={
        <InvoiceDrawerFooter
          state={state}
          setState={setState}
          invoiceQuery={invoiceQuery}
          updateInvoiceMutation={updateInvoiceMutation}
          updateInvoiceNotesMutation={updateInvoiceNotesMutation}
          updateInvoicePaymentDateMutation={updateInvoicePaymentDateMutation}
          isPaymentDateDirty={isPaymentDateDirty}
          handleClose={handleClose}
        />
      }
      size='xl'
    >
      <MantineTabs
        value={activeTab}
        onTabChange={setActiveTab}
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <MantineTabs.List p='md' pb={0} sx={{ flexWrap: 'nowrap' }}>
          {tabs.map(item => (
            <MantineTabs.Tab key={item.value} value={item.value} disabled={item.isDisabled}>
              {item.label}
            </MantineTabs.Tab>
          ))}
        </MantineTabs.List>

        {tabs.map(item => (
          <MantineTabs.Panel key={item.value} value={item.value} p='xl' style={{ flexGrow: 1 }}>
            {item.component}
          </MantineTabs.Panel>
        ))}
      </MantineTabs>
    </Drawer>
  );
};

export default InvoiceDrawer;
